import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { TenantLifecycleService } from '../tenant/tenant-lifecycle.service';
import { UserApiService } from '../../shared/services/api/user-api.service';
import { USER_TYPES_MANAGE } from './user-types.constants';

/**
 * Route guard for the user-type management surface (`/admin/user-types`).
 *
 * Reads the caller's advertised permissions from `GET /user/auth/me` and
 * activates the route only if `user_types.manage` is present. Everyone else —
 * including super_users, to whom the API never advertises the key — is bounced
 * to `/home`. Mirrors `userPermissionsAdminGuard`, different permission key.
 *
 * `/me` is tenant-scoped: the response carries the keys the caller holds *in
 * the active tenant*, so the tenant must be resolved before the read. The
 * guard awaits `TenantLifecycleService.bootstrapTenantContext()` first for
 * that reason, matching `configurationNavEnabledGuard` — without it a cold
 * deep-link resolves against whatever tenant key happens to be set at that
 * instant and can bounce a real admin to `/home`. The bootstrap stream is
 * memoized via `shareReplay`, so this await joins AppLayout's in-flight
 * bootstrap rather than issuing a second fetch.
 */
export const userTypesAdminGuard: CanActivateFn = async () => {
  const userApi = inject(UserApiService);
  const router = inject(Router);
  const lifecycle = inject(TenantLifecycleService);
  try {
    await firstValueFrom(lifecycle.bootstrapTenantContext());
    const me = await firstValueFrom(userApi.getUserData());
    if (me.permissions?.includes(USER_TYPES_MANAGE)) {
      return true;
    }
  } catch {
    // Network / auth issues, or a bootstrap that found no tenant to act in,
    // fall through to redirect — the authTokenInterceptor already handles the
    // 401/403 cases at the HTTP layer.
  }
  return router.createUrlTree(['/home']);
};
